import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

interface Badge {
  id: number;
  uuid: string;
  name: string;
  image_url: string;
  earned_at: string;
  expires_at: string | null;
  verification_url: string;
}

interface Skillstree {
  id: number;
  name: string;
  code: string | null;
  slug: string;
  image_url: string | null;
  description: string | null;
}

interface Passport {
  id: number;
  unique_code: string;
  skillstree: Skillstree;
  badges: Badge[];
  completed_at: string;
  verification_url: string;
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  country: string | null;
}

interface Statistics {
  total_badges: number;
  total_passports: number;
}

interface ProfileData {
  fullName: string;
  credentials: any[];
  user?: User;
  statistics?: Statistics;
  badges?: Badge[];
  passports?: Passport[];
  [key: string]: any;
}

interface SelectedCredential {
  type?: string;
  id?: string;
  uuid?: string;
  unique_code?: string;
  name?: string;
  title?: string;
  image_url?: string;
  earned_at?: string;
  expires_at?: string | null;
  verification_url?: string;
  skillstree?: Skillstree;
  badges?: Badge[];
  completed_at?: string;
  [key: string]: any;
}

import './PublicProfile.css';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ProfileHeader from '@/components/profile/ProfileHeader';
import CredentialsTabs from '@/components/profile/CredentialsTabs';
import BadgeDetails from '@/components/profile/BadgeDetails';
import PassportDetails from '@/components/profile/PassportDetails';
import { getPublicProfile, getCredentialDetails } from '@/services/api';

const PublicProfilePage = () => {
  const { username, tab: urlTab, credentialId: urlCredentialId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCredential, setSelectedCredential] = useState<SelectedCredential | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  let fullName = profileData?.fullName || profileData?.first_name || '';
  fullName = profileData?.last_name ? `${fullName} ${profileData.last_name}` : fullName;

  useEffect(() => {
    fetchProfileData();
  }, [username]);

  useEffect(() => {
    // Handle URL parameters when profile data is loaded
    if (profileData && !loading) {
      handleUrlParameters();
    }
  }, [profileData, loading]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      setError('');
      // console.log('username:', username);
      const response = await getPublicProfile(username as any);
      // console.log('ProfileData response:', response);
      setProfileData(formatData(response?.data as any));
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatData = (data: any) => {
    // console.log('data:', data.user);
    // console.log('is data:', (data === null || data === undefined) && Object.keys(data).length > 0);
    if((data === null || data === undefined) && Object.keys(data).length === 0) {
      return null;
    }

    const user = data.user || {};
    const badges = data?.badges?.length ? data?.badges.map(b => ({...b, type: 'badges'})) : {};
    const passports = data?.passports?.length ? data?.passports.map(p => ({...p, type: 'passports'})) : {};
    const statistics = data.statistics || {};
    // console.log('user:', user);
    // console.log('badges:', badges);
    // console.log('passports:', passports);
    // console.log('statistics:', statistics);

    return {credentials: [...badges, ...passports], ...user, ...statistics};
  };
  
  const handleUrlParameters = async () => {
    if (!profileData) return;

      // console.log('handleUrlParameters response:', profileData);

    // Set active tab from URL
    if (urlTab && ['all', 'badges', 'passports'].includes(urlTab)) {
      setActiveTab(urlTab);
    }

    // Handle credential ID from URL
    if (urlCredentialId && profileData.credentials) {
      const credential = profileData.credentials.find(
        cred => cred.id === urlCredentialId || 
                cred.credentialId === urlCredentialId || 
                cred.uuid === urlCredentialId ||
                cred.unique_code === urlCredentialId
      );

      if (credential) {
        try {
          setLoading(true);
          // const response = await getCredentialDetails(
          //   credential.id || credential.uuid || credential.unique_code, 
          //   credential.type
          // );
          setSelectedCredential(credential);
        } catch (error) {
          console.error('Error fetching credential details:', error);
          setError('Failed to load credential details.');
          // Redirect to profile without credential ID
          navigate(`/profile/${username}`, { replace: true });
        } finally {
          setLoading(false);
        }
      } else {
        setError(`Credential with ID ${urlCredentialId} not found for ${username}`);
        // Redirect to profile without credential ID
        navigate(`/profile/${username}`, { replace: true });
      }
    }
  };
  
  const handleCredentialClick = async (credential: any) => {
    try {
      setLoading(true);
      console.log('handleCredentialClick:', credential);
      setSelectedCredential(credential);
      // const response = await getCredentialDetails(credentialId, type);
      // setSelectedCredential({ ...response.data, type });
      
      // Update URL with credential ID
      // navigate(`/profile/${username}/${type}/${credentialId}`, { replace: true });
    } catch (error) {
      console.error('Error fetching credential details:', error);
      setError('Failed to load credential details.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToProfile = () => {
    setSelectedCredential(null);
    setError('');
    // Reset URL to profile without credential ID
    navigate(`/profile/${username}`, { replace: true });
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    // Update URL with tab
    if (selectedCredential) {
      navigate(`/profile/${username}/${tab}`, { replace: true });
      setSelectedCredential(null);
    }
  };

  if (loading && !profileData) {
    return (
      <div className="min-h-screen bg-transparent text-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-50">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error && !profileData) {
    return (
      <div className="min-h-screen bg-transparent text-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-100">Error</h2>
          <p className="text-gray-50 mt-2">{error}</p>
          <button 
            onClick={fetchProfileData}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen bg-transparent text-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-100">Profile not found</h2>
          <p className="text-gray-50 mt-2">The requested profile could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-profile-container text-start">
      <header className="public-profile-header text-xs max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="block font-extrabold">
          Public Profile Page
        </h1>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}
        <ProfileHeader 
          profileData={profileData}
          username={username}
        />
        
        {/* Show tabs even when viewing credential details */}
        {selectedCredential && (
          <div className="mb-6">
            <CredentialsTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              credentials={profileData.credentials}
              onCredentialClick={handleCredentialClick}
              showTabsOnly={true}
            />
          </div>
        )}

        {selectedCredential ? (
          <>
            {selectedCredential.type === 'badges' ? (
              <BadgeDetails
                onTabChange={handleTabChange}
                credential={selectedCredential}
                onBack={handleBackToProfile}
                username={username}
                profileName={fullName}
              />
            ) : (
              <PassportDetails
                onTabChange={handleTabChange}
                credential={selectedCredential}
                onBack={handleBackToProfile}
                username={username}
                profileName={fullName}
              />
            )}
          </>
        ) : (
          <CredentialsTabs
            activeTab={activeTab}
            onTabChange={handleTabChange}
            credentials={profileData.credentials}
            onCredentialClick={handleCredentialClick}
          />
        )}
      </main>
    </div>
  );
};

export default PublicProfilePage;